import classNames from 'classnames';
import { formatCurrency } from 'lib/helpers/strings';
import HeadHunterAvatar from 'modules/App/components/HeadHunterAvatar';
import { useAuthModal } from 'modules/auth';
import Table, { ColumnDef } from 'modules/common/components/Table';
import Areas from 'modules/geo/components/Areas';
import CountryEmojis from 'modules/geo/data-components/CountryEmojis';
import { useRouter } from 'next/router';
import { parseAsString, useQueryState } from 'nuqs';
import React from 'react';
import { MdGroup, MdOutlineArrowForwardIos as IconView, MdGridView, MdMap, MdFilterList } from 'react-icons/md';
import UserStats from '../components/UserStats';

import { gql, useQuery } from 'modules/GraphQL';
import dynamic from 'next/dynamic';
import { Button } from 'lib/components/ui/button';
import { User } from '../interfaces';
import { Badge } from '@/lib/components/ui/badge';
import { MdPersonSearch as IconHighlightedOpenToSource } from 'react-icons/md';
import { RxStarFilled as IconHighlighted } from 'react-icons/rx';
import SearchItems, { useSearchFilterQuery } from 'modules/common/components/SearchItems';
import { companySizes } from 'modules/User/models/constants';
const MapComponent = dynamic(() => import('lib/components/ui/MapComponent'), {
  ssr: false,
});

const LIST_MEMBERS = gql`
  query Members($sort: String, $page: NonNegativeInt!, $limit: NonNegativeInt!, $filter: UsersFilterInput) {
    recruiters(sort: $sort, page: $page, limit: $limit, filter: $filter) {
      pageInfo {
        count
        page
        limit
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      items {
        _id
        name
        image
        email
        title
        url
        bio
        phone
        companyLocation
        countryIds
        sectorIds
        sector
        sectors {
          _id
          name
        }
        occupationIds
        occupation
        occupations {
          _id
          name
        }
        companyArea
        companySalaryTreated {
          min
          max
        }
        areaSearch {
          areas {
            name
            radius
            geo {
              type
              coordinates
            }
          }
        }
        locations {
          areas {
            name
            radius
            geo {
              type
              coordinates
            }
          }
        }
        companyClients
        companySize
        businessRegistrationId
        createdAt
        updatedAt
        isTop
        openToSource
        ambassadorFor
        nbSearchedProfiles
        nbAvailableProfiles
        nbSearchedProfilesPublished
        nbAvailableProfilesPublished
        nbContactsProspecting
        nbContactsSourcing
        nbQualityContactsProspecting
        nbQualityContactsSourcing
        lastLoginAt
      }
    }
  }
`;

type MemberListsProps = React.HTMLAttributes<HTMLDivElement> & {
  recruiters: User[];
  maxItems?: number;
};

const RecruitersList = ({ recruiters: initialItems = [] }: MemberListsProps) => {
  const router = useRouter();
  const { session, toggleModal } = useAuthModal();

  const [displayMode, setDisplayMode] = useQueryState('displayMode', parseAsString.withDefault('map'));
  const { filter: filterQuery } = useSearchFilterQuery();
  const { data } = useQuery(LIST_MEMBERS, {
    variables: { page: 1, limit: displayMode === 'map' ? 10000 : 0, filter: { ...filterQuery } },
  });

  const items = data?.recruiters?.items || initialItems;

  const count = displayMode === 'map' ? items.filter((item: any) => item.locations?.areas?.length > 0).length : items.length || 0;

  const details = React.useCallback(
    (itemId?: string) => (e: any) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (!!session) {
        router.push(`/members/?_id=${itemId}`);
      } else {
        toggleModal();
      }
    },
    [session, router, toggleModal]
  );

  const columns = React.useMemo<ColumnDef<User>[]>(
    () => [
      {
        header: 'Consultant',
        accessorKey: 'name',
        cell: ({ row: { original: user } }) => {
          const { companySize } = user;
          return (
            <>
              <div className="flex items-center gap-2">
                <HeadHunterAvatar
                  user={{
                    name: 'Identité cachée',
                  }}
                  withCompany={false}
                />
              </div>
              <small className="flex gap-2 items-center ml-5">
                <span className="flex items-center gap-0.5">
                  <MdGroup />
                  {companySizes[companySize as keyof typeof companySizes]} collaborateurs
                </span>
              </small>
              {user?.isTop && (
                <div>
                  <Badge variant="outline-primary" className="mt-1">
                    <IconHighlighted className="text-primary h-4 w-4 stroke-white stroke-1" /> Top Headlinker
                  </Badge>
                </div>
              )}
              {user?.openToSource && (
                <div>
                  <Badge variant="outline-accent" className="mt-1">
                    <IconHighlightedOpenToSource className="text-accent h-4 w-4 stroke-white stroke-1" /> Dispo pour sourcer !
                  </Badge>
                </div>
              )}
            </>
          );
        },
      },
      {
        header: 'Périmètre d’intervention',
        accessorKey: 'createdBy',
        cell: ({ row: { original: user } }) => {
          const { companySalaryTreated, countryIds, areaSearch } = user;
          return (
            <>
              {companySalaryTreated && (
                <small className="block">
                  {formatCurrency({ locale: 'fr', value: +companySalaryTreated?.min, precision: 2, compact: true })} -{' '}
                  {formatCurrency({ locale: 'fr', value: +companySalaryTreated?.max, precision: 2, compact: true })}
                </small>
              )}
              <CountryEmojis countryIds={countryIds} />
              <Areas className="[&>span>span]:max-w-[200px] " items={areaSearch?.areas || []} />
            </>
          );
        },
      },
      {
        header: 'Expertises / Métiers / Clients',
        accessorKey: 'searcher',
        cell: ({ row: { original: user } }) => {
          const { sector, occupation } = user;
          return (
            <>
              <span className="block max-w-[200px] truncate text-xs font-semibold" title={sector}>
                {sector}
              </span>
              <span className="block max-w-[200px] truncate text-xs font-normal" title={occupation}>
                {occupation}
              </span>
            </>
          );
        },
      },
      {
        header: 'Activité',
        accessorKey: 'nbContacts',
        cell: ({ row: { original: user } }) => {
          return <UserStats user={{ ...user, createdAt: user.createdAt2 }} className="leading-snug"></UserStats>;
        },
      },
      {
        header: ' ',
        align: 'right',
        cell: ({ row: { original: user } }) => {
          const { _id } = user;
          return (
            <button className="btn btn-primary btn-sm gap-1 w-auto sm:w-24" onClick={details(_id)}>
              <IconView />
              <span className="hidden sm:inline-block">Voir</span>
            </button>
          );
        },
      },
    ],
    [session?.user?.id, details]
  );

  const allAreas = React.useMemo(
    () =>
      items.flatMap(
        (user: any) =>
          user.locations?.areas?.map((area: any) => ({
            ...area,
            key: user._id,
            icon: `https://placehold.co/24/333333/FFFFFF?text=${encodeURIComponent(user.name.replace(' ', ''))}`,
            onClick: details(user._id),
            className: classNames(
              'rounded-full border-4 shadow-md border-solid hover:border-black',
              !!area.ambassador ? 'border-accent' : user.isTop ? 'border-primary' : user.openToSource ? 'border-accent' : 'border-white'
            ),
            tooltip: (
              <div className="text-base py-1 px-3 mb-1">
                <HeadHunterAvatar
                  user={{
                    name: 'Identité cachée',
                  }}
                  withCompany={false}
                />
                {user?.isTop && (
                  <div>
                    <Badge variant="outline-primary" className="mt-1">
                      <IconHighlighted className="text-primary h-4 w-4 stroke-white stroke-1" /> Top Headlinker
                    </Badge>
                  </div>
                )}
                {user?.openToSource && (
                  <div>
                    <Badge variant="outline-accent" className="mt-1">
                      <IconHighlightedOpenToSource className="text-accent h-4 w-4 stroke-white stroke-1" /> Dispo pour sourcer !
                    </Badge>
                  </div>
                )}
                <div className="text-sm mt-2">
                  <div className="font-bold">Ville</div>
                  {area.name}
                </div>
                <div className="text-sm mt-2">
                  <div className="font-bold">Zones de recherche</div>
                  <CountryEmojis countryIds={user.countryIds} />
                  <Areas className="[&>span>span]:w-[250px] text-xs whitespace-normal" items={user.areaSearch?.areas || []} />
                </div>
                <div className="text-sm mt-2">
                  <div className="font-bold">Secteur{user.sectorIds?.length === 1 ? '' : 's'}</div>
                  <div className="w-[250px] text-xs whitespace-normal" title={user.sector}>
                    {user.sector}
                  </div>
                </div>
                <div className="text-sm mt-2">
                  <div className="font-bold">Métier{user.occupationIds?.length === 1 ? '' : 's'}</div>
                  <div className="w-[250px] text-xs whitespace-normal" title={user.occupation}>
                    {user.occupation}
                  </div>
                </div>
                <div className="text-sm mt-2">
                  <div className="font-bold">Activité</div>
                  <UserStats user={{ ...user, createdAt: user.createdAt2 }} className="leading-snug"></UserStats>
                </div>
              </div>
            ),
          })) || []
      ),
    [items, details]
  );

  return (
    <>
      <div className="flex items-center gap-2 bg-gray-50 px-1 sm:px-2">
        <MdFilterList className="h-7 w-7 " />
        <SearchItems placeholder="Recherche" className="my-3 mx-2" withCountry={false} withArea={false} />
        <span className="text-sm text-gray-500">
          <strong>{items.length}</strong> recruteurs
        </span>
        <div className="ml-auto">
          {displayMode === 'map' ? (
            <Button variant="outline" size="sm" onClick={() => setDisplayMode('table')}>
              <MdGridView className="w-4 h-4" />
              Voir la liste
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setDisplayMode('map')}>
              <MdMap className="w-4 h-4" />
              Voir sur la carte
            </Button>
          )}
        </div>
      </div>
      {displayMode === 'map' ? (
        <MapComponent areas={allAreas} className="my-4 rounded shadow border" style={{ width: '100%', height: 800 }} />
      ) : (
        <div className="max-w-6xl mx-auto">
          <div className="max-h-96 overflow-y-auto mb-12">
            <Table columns={columns} data={items} count={count} limit={1000} page={1} loading={false} />
          </div>
        </div>
      )}
    </>
  );
};

export default RecruitersList;
